import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import LookupSelect from '../components/ui/LookupSelect';
import ContactSelect from '../components/ui/ContactSelect';
import AcquisitionTypeToggle from '../components/ui/AcquisitionTypeToggle';
import { formatDate, todayLocal } from '../lib/utils';
import SafeIcon from '../common/SafeIcon';
import {
  FiArrowLeft, FiPackage, FiCheckCircle, FiXCircle, FiAlertCircle, FiUser, FiMapPin, FiTag, FiHash, FiCalendar, FiExternalLink, FiShield
} from 'react-icons/fi';

// Resolve the dealer's display name (business name falls back to licensee name).
function dealerName(d) {
  return d?.business_name || d?.license_name || d?.ffl_dashed || d?.ffl_number || '';
}

// The dealer address may arrive structured ({street,city,state,zip}) or as a
// single formatted line — render either.
function dealerAddress(d) {
  if (!d) return '';
  const a = d.address;
  if (a && typeof a === 'object') {
    const cityState = [a.city, a.state].filter(Boolean).join(', ');
    return [a.street, cityState, a.zip].filter(Boolean).join(', ');
  }
  return d.address_line || (typeof a === 'string' ? a : '');
}

function statusBadgeVariant(status) {
  switch (status) {
    case 'PENDING': return 'warning';
    case 'RECEIVED': return 'primary';
    case 'CONVERTED': return 'success';
    case 'CANCELED': return 'error';
    default: return 'default';
  }
}

function FieldRow({ label, value, mono }) {
  return (
    <div className="flex flex-col gap-0.5 py-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <span className={`text-sm text-slate-700 dark:text-slate-200 ${mono ? 'font-mono' : ''}`}>
        {value || <span className="text-slate-400">—</span>}
      </span>
    </div>
  );
}

export default function ReceivePendingOrder() {
  const { id } = useParams();
  const { tenant, user } = useAuth();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Verify form state
  const [verified, setVerified] = useState({
    manufacturer_id: '',
    importer_id: '',
    model_id: '',
    type_id: '',
    caliber_id: '',
    serial_number: '',
    is_non_serialized: false,
    acquired_from_id: '',
    acquisition_date: todayLocal(),
    acquisition_type: 'REPAIR'
  });

  const [marking, setMarking] = useState(false);
  const [converting, setConverting] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const isInspector = user?.role === 'INSPECTOR';
  const isTerminal = order && ['CONVERTED', 'CANCELED'].includes(order.status);
  const canMutate = !isInspector && !isTerminal;
  const canConvert = canMutate && order?.status === 'RECEIVED';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await api.getPendingOrder(id);
        if (cancelled) return;
        if (!data) {
          setError('Pending order not found.');
          return;
        }
        setOrder(data);
        // Pre-fill verified fields from declared + guesses
        setVerified((v) => ({
          ...v,
          manufacturer_id: data.guessed_manufacturer_id || '',
          model_id: data.guessed_model_id || '',
          type_id: data.guessed_type_id || '',
          caliber_id: data.guessed_caliber_id || '',
          serial_number: data.declared_serial_number || '',
          is_non_serialized: !!data.declared_is_non_serialized,
          acquired_from_id: data.guessed_customer_id || ''
        }));
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load order');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  const fullDeclaredAddress = (o) => {
    if (!o) return '';
    const lines = [
      o.declared_address_line1,
      o.declared_address_line2,
      [o.declared_city, o.declared_state, o.declared_postal_code].filter(Boolean).join(', '),
      o.declared_country
    ].filter(Boolean);
    return lines.join('\n');
  };

  // Single-line address for the customers table (which has a single text field).
  const singleLineDeclaredAddress = (o) => {
    if (!o) return '';
    return [
      [o.declared_address_line1, o.declared_address_line2].filter(Boolean).join(', '),
      [o.declared_city, o.declared_state, o.declared_postal_code].filter(Boolean).join(', '),
      o.declared_country && o.declared_country !== 'US' ? o.declared_country : null
    ].filter(Boolean).join(', ');
  };

  // Defaults handed to ContactSelect when the user clicks "Add New Contact".
  // If both `name` and `contact_name` came through, the source is most likely
  // a dealer (business name + person on file) — pre-select DEALER. Otherwise
  // assume CUSTOMER. Staff can flip the toggle in the modal if needed.
  const looksLikeDealer = !!(order?.declared_customer_name?.trim()
    && order?.declared_contact_name?.trim());
  const contactDefaults = order ? {
    type: looksLikeDealer ? 'DEALER' : 'CUSTOMER',
    name: order.declared_customer_name || '',
    contact_name: order.declared_contact_name || '',
    email: order.declared_email || '',
    phone: order.declared_phone || '',
    address: singleLineDeclaredAddress(order)
  } : undefined;

  const handleMarkReceived = async () => {
    setMarking(true);
    try {
      const updated = await api.markPendingOrderReceived(order.id, user.id);
      setOrder(updated);
    } catch (err) {
      alert(`Error: ${err.message || err}`);
    } finally {
      setMarking(false);
    }
  };

  const handleCancel = async () => {
    const reason = prompt('Reason for cancellation (optional):');
    if (reason === null) return; // user dismissed
    setCanceling(true);
    try {
      const updated = await api.cancelPendingOrder(order.id, reason || null);
      setOrder(updated);
    } catch (err) {
      alert(`Error: ${err.message || err}`);
    } finally {
      setCanceling(false);
    }
  };

  const handleConvert = async (e) => {
    e?.preventDefault?.();
    if (order?.status !== 'RECEIVED') {
      alert('Mark the order as Received first — that confirms the rifle has physically arrived. Then you can verify and convert.');
      return;
    }
    if (!verified.manufacturer_id || !verified.model_id || !verified.type_id || !verified.caliber_id) {
      alert('Manufacturer, model, type, and caliber are required.');
      return;
    }
    if (!verified.is_non_serialized && !verified.serial_number.trim()) {
      alert('Serial number is required for serialized firearms.');
      return;
    }
    if (!verified.acquired_from_id) {
      alert('A contact must be selected for the acquisition source.');
      return;
    }
    if (!verified.acquisition_date) {
      alert('Acquisition date is required.');
      return;
    }

    if (!confirm('Convert this pending order to a permanent bound book acquisition? This action cannot be undone.')) return;

    setConverting(true);
    try {
      const result = await api.convertPendingOrderToAcquisition(order.id, {
        manufacturer_id: verified.manufacturer_id,
        importer_id: verified.importer_id || null,
        model_id: verified.model_id,
        type_id: verified.type_id,
        caliber_id: verified.caliber_id,
        serial_number: verified.serial_number,
        is_non_serialized: verified.is_non_serialized,
        acquisition_date: verified.acquisition_date,
        acquisition_type: verified.acquisition_type,
        customer_id: verified.acquired_from_id
      });
      // result is { pending_id, firearm_id, entry_id, customer_id }
      if (result?.entry_id) {
        navigate(`/bound-book?highlight=${result.entry_id}`);
      } else {
        navigate('/bound-book');
      }
    } catch (err) {
      alert(`Error converting: ${err.message || err}`);
      setConverting(false);
    }
  };

  if (loading) {
    return <div className="p-12 text-center text-sm text-slate-500">Loading…</div>;
  }
  if (error || !order) {
    return (
      <div className="max-w-xl mx-auto py-12 text-center">
        <SafeIcon icon={FiAlertCircle} className="mx-auto h-10 w-10 text-red-500" />
        <p className="mt-3 text-sm text-slate-700 dark:text-slate-200">{error || 'Order not found.'}</p>
        <Button className="mt-4" variant="secondary" icon={FiArrowLeft} onClick={() => navigate('/incoming-orders')}>
          Back to Incoming Orders
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={() => navigate('/incoming-orders')}
            className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 mb-2"
          >
            <SafeIcon icon={FiArrowLeft} className="h-4 w-4 mr-1" />
            Back to Incoming Orders
          </button>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            Order #{order.external_order_id}
          </h2>
          <div className="mt-1 flex items-center gap-2 text-sm text-slate-500">
            <span>Received via {order.external_source}</span>
            {order.external_order_url && (
              <a
                href={order.external_order_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700"
              >
                View in WooCommerce
                <SafeIcon icon={FiExternalLink} className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Badge variant={statusBadgeVariant(order.status)}>{order.status}</Badge>
          {order.received_at && (
            <span className="text-xs text-slate-500">Received {formatDate(order.received_at)}</span>
          )}
          {order.converted_at && (
            <span className="text-xs text-slate-500">Converted {formatDate(order.converted_at)}</span>
          )}
        </div>
      </div>

      {/* Inspector banner */}
      {isInspector && (
        <Card>
          <CardContent className="py-3 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <SafeIcon icon={FiAlertCircle} className="h-4 w-4 text-slate-400" />
            Inspector view — read-only.
          </CardContent>
        </Card>
      )}

      {/* Action bar */}
      {canMutate && (
        <Card>
          <CardContent className="py-4 flex flex-wrap items-center gap-2">
            {order.status === 'PENDING' && (
              <Button icon={FiPackage} onClick={handleMarkReceived} isLoading={marking}>
                Mark Received
              </Button>
            )}
            <Button
              icon={FiCheckCircle}
              variant={canConvert ? 'primary' : 'secondary'}
              onClick={handleConvert}
              isLoading={converting}
              disabled={!canConvert || converting}
              title={!canConvert ? 'Mark the order Received first.' : undefined}
            >
              Verify &amp; Convert to Acquisition
            </Button>
            <Button variant="secondary" icon={FiXCircle} onClick={handleCancel} isLoading={canceling} className="text-red-600">
              Cancel Order
            </Button>
            <span className="text-xs text-slate-500 ml-auto">
              {order.status === 'PENDING'
                ? 'When the rifle arrives, click Mark Received. Then verify the details and convert.'
                : 'Verify the rifle in hand matches the declared info, correct anything that differs, then convert.'}
            </span>
          </CardContent>
        </Card>
      )}

      {/* FFL transfer dealer (when the order used the dealer selector) */}
      {order.ffl_dealer && (
        <Card className="border-primary-200 dark:border-primary-900">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SafeIcon icon={FiShield} className="h-5 w-5 text-primary-600" />
              FFL Transfer Dealer
            </CardTitle>
            <p className="text-xs text-slate-500">
              The receiving FFL the customer selected at checkout — where this firearm ships for transfer.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              <FieldRow label="Dealer" value={dealerName(order.ffl_dealer)} />
              <FieldRow
                label="FFL Number"
                mono
                value={order.ffl_dealer.ffl_dashed || order.ffl_dealer_number || order.ffl_dealer.ffl_number}
              />
              <FieldRow label="Phone" value={order.ffl_dealer.phone} />
              <FieldRow
                label="FFL Expires"
                value={order.ffl_dealer.expiration_date ? formatDate(order.ffl_dealer.expiration_date) : null}
              />
            </div>
            <FieldRow label="Address" value={dealerAddress(order.ffl_dealer)} />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {order.ffl_dealer.verify_source && (
                <Badge variant={order.ffl_dealer.verify_source === 'revalidated' ? 'success' : 'warning'}>
                  {order.ffl_dealer.verify_source === 'revalidated' ? 'Verified at checkout' : 'Unverified'}
                  {order.ffl_dealer.verified_at ? ` · ${formatDate(order.ffl_dealer.verified_at)}` : ''}
                </Badge>
              )}
              {typeof order.ffl_dealer.split_shipment === 'boolean' && (
                <Badge variant="default">
                  {order.ffl_dealer.split_shipment
                    ? 'Split shipment — non-firearm items to buyer'
                    : 'Full order ships to FFL'}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Two-column verify view */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: Declared (read-only) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SafeIcon icon={FiUser} className="h-5 w-5 text-slate-400" />
              Declared by Customer
            </CardTitle>
            <p className="text-xs text-slate-500">As entered at WooCommerce checkout — verify against the rifle and packaging.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                <SafeIcon icon={FiUser} className="h-3.5 w-3.5" /> Customer
              </h4>
              <div className="rounded-md border border-slate-200 dark:border-slate-800 p-3 bg-slate-50/40 dark:bg-slate-800/30">
                <FieldRow label="Name" value={order.declared_customer_name} />
                <FieldRow label="Contact" value={order.declared_contact_name} />
                <FieldRow label="Email" value={order.declared_email} />
                <FieldRow label="Phone" value={order.declared_phone} />
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                <SafeIcon icon={FiMapPin} className="h-3.5 w-3.5" /> Shipping Address
              </h4>
              <div className="rounded-md border border-slate-200 dark:border-slate-800 p-3 bg-slate-50/40 dark:bg-slate-800/30 whitespace-pre-line text-sm text-slate-700 dark:text-slate-200">
                {fullDeclaredAddress(order) || <span className="text-slate-400">—</span>}
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                <SafeIcon icon={FiTag} className="h-3.5 w-3.5" /> Declared Firearm
              </h4>
              <div className="rounded-md border border-slate-200 dark:border-slate-800 p-3 bg-slate-50/40 dark:bg-slate-800/30">
                <FieldRow label="Manufacturer" value={order.declared_manufacturer} />
                <FieldRow label="Model" value={order.declared_model} />
                <FieldRow label="Caliber" value={order.declared_caliber} />
                <FieldRow label="Type" value={order.declared_type} />
                <FieldRow
                  label="Serial Number"
                  mono
                  value={order.declared_is_non_serialized ? 'NSN (non-serialized)' : order.declared_serial_number}
                />
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                <SafeIcon icon={FiCalendar} className="h-3.5 w-3.5" /> Service / Order
              </h4>
              <div className="rounded-md border border-slate-200 dark:border-slate-800 p-3 bg-slate-50/40 dark:bg-slate-800/30">
                <FieldRow label="Service" value={order.service_description} />
                <FieldRow label="SKU" value={order.service_sku} />
                <FieldRow label="Expected Arrival" value={order.expected_arrival_date ? formatDate(order.expected_arrival_date) : null} />
                <FieldRow label="Customer Notes" value={order.customer_notes} />
              </div>
            </div>

            {order.cancel_reason && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
                <strong>Canceled:</strong> {order.cancel_reason}
              </div>
            )}
          </CardContent>
        </Card>

        {/* RIGHT: Verify form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-primary-600" />
              Verify &amp; Acquire
            </CardTitle>
            <p className="text-xs text-slate-500">Pre-filled from declared data. Correct anything that doesn't match the rifle in hand.</p>
          </CardHeader>
          <CardContent>
            <fieldset disabled={!canMutate} className="space-y-5">
              <div className="grid grid-cols-1 gap-4">
                <LookupSelect
                  table="manufacturers"
                  label="Manufacturer"
                  value={verified.manufacturer_id}
                  onChange={(val) => setVerified((v) => ({ ...v, manufacturer_id: val, model_id: '' }))}
                  required
                />
                <LookupSelect
                  table="models"
                  label="Model"
                  value={verified.model_id}
                  onChange={(val) => setVerified((v) => ({ ...v, model_id: val }))}
                  filters={{ manufacturer_id: verified.manufacturer_id }}
                  createExtras={{ manufacturer_id: verified.manufacturer_id }}
                  disabled={!verified.manufacturer_id}
                  required
                />
                <LookupSelect
                  table="types"
                  label="Firearm Type"
                  value={verified.type_id}
                  onChange={(val) => setVerified((v) => ({ ...v, type_id: val }))}
                  required
                />
                <LookupSelect
                  table="calibers"
                  label="Caliber"
                  value={verified.caliber_id}
                  onChange={(val) => setVerified((v) => ({ ...v, caliber_id: val }))}
                  required
                />
              </div>

              <div className="border-t border-slate-100 dark:border-slate-800 pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    id="rcv_is_non_serialized"
                    checked={verified.is_non_serialized}
                    onChange={(e) => setVerified((v) => ({
                      ...v,
                      is_non_serialized: e.target.checked,
                      serial_number: e.target.checked ? '' : v.serial_number
                    }))}
                    className="h-4 w-4 rounded border-slate-300 text-primary-500 focus:ring-primary-500"
                  />
                  <label htmlFor="rcv_is_non_serialized" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Non-Serialized (NSN)
                  </label>
                </div>
                {verified.is_non_serialized ? (
                  <div className="flex h-10 w-full items-center rounded-md border border-slate-300 bg-slate-100 px-3 text-sm text-slate-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400">
                    NSN — No Serial Number
                  </div>
                ) : (
                  <Input
                    label={
                      <span className="inline-flex items-center gap-1.5">
                        <SafeIcon icon={FiHash} className="h-3.5 w-3.5" />
                        Serial Number (verified from rifle)
                      </span>
                    }
                    value={verified.serial_number}
                    onChange={(e) => setVerified((v) => ({ ...v, serial_number: e.target.value }))}
                    placeholder="Read directly from receiver"
                  />
                )}
                {!verified.is_non_serialized
                  && order.declared_serial_number
                  && verified.serial_number
                  && verified.serial_number.trim().toUpperCase() !== order.declared_serial_number.trim().toUpperCase() && (
                    <div className="mt-1.5 inline-flex items-center gap-1 text-xs text-yellow-700 dark:text-yellow-400">
                      <SafeIcon icon={FiAlertCircle} className="h-3.5 w-3.5" />
                      Differs from declared serial: {order.declared_serial_number}
                    </div>
                )}
              </div>

              <div className="border-t border-slate-100 dark:border-slate-800 pt-4">
                <Input
                  label="Acquisition Date"
                  type="date"
                  value={verified.acquisition_date}
                  onChange={(e) => setVerified((v) => ({ ...v, acquisition_date: e.target.value }))}
                  required
                />
              </div>

              <div className="border-t border-slate-100 dark:border-slate-800 pt-4">
                <AcquisitionTypeToggle
                  value={verified.acquisition_type}
                  onChange={(val) => setVerified((v) => ({ ...v, acquisition_type: val }))}
                />
              </div>

              <div className="border-t border-slate-100 dark:border-slate-800 pt-4">
                <h4 className="text-sm font-semibold mb-2 text-slate-900 dark:text-white">Source Contact</h4>
                <p className="text-xs text-slate-500 mb-3">
                  Pick an existing contact, or use "Add new contact" — pre-fill it with the declared customer info above.
                </p>
                <ContactSelect
                  label="Acquired From"
                  value={verified.acquired_from_id}
                  onChange={(val) => setVerified((v) => ({ ...v, acquired_from_id: val }))}
                  required
                  placeholder="Select customer / source"
                  defaults={contactDefaults}
                />
              </div>

              {canMutate && (
                <div className="border-t border-slate-100 dark:border-slate-800 pt-4">
                  <Button
                    type="button"
                    onClick={handleConvert}
                    isLoading={converting}
                    icon={FiCheckCircle}
                    disabled={!canConvert || converting}
                    title={!canConvert ? 'Mark the order Received first.' : undefined}
                    className="w-full py-5 text-base"
                  >
                    Verify &amp; Convert to Acquisition
                  </Button>
                  <p className="mt-2 text-center text-xs text-slate-500">
                    {canConvert
                      ? 'This creates a permanent federal record. Verify all fields match the physical firearm.'
                      : 'Click Mark Received above once the rifle physically arrives — then you can convert.'}
                  </p>
                </div>
              )}
            </fieldset>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
